import { useNavigate, useLocation, useParams as useRxParams } from 'react-router-dom';
import { useMemo, useSyncExternalStore, useCallback } from 'react';

export function useRouter() {
  const navigate = useNavigate();
  return useMemo(() => ({
    push: (href: string) => navigate(href),
    replace: (href: string) => navigate(href, { replace: true }),
    back: () => navigate(-1),
    forward: () => navigate(1),
    refresh: () => window.location.reload(),
    prefetch: () => {},
  }), [navigate]);
}

export function usePathname() {
  const { pathname } = useLocation();
  return pathname;
}

// Global listener setup for URL changes (singleton pattern)
let urlChangeListeners: Set<() => void> = new Set();
let isHistoryPatched = false;

function patchHistory() {
  if (isHistoryPatched) return;
  isHistoryPatched = true;

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = function(...args) {
    originalPushState(...args);
    urlChangeListeners.forEach(listener => listener());
  };

  history.replaceState = function(...args) {
    originalReplaceState(...args);
    urlChangeListeners.forEach(listener => listener());
  };

  // Also listen for popstate (back/forward navigation)
  window.addEventListener('popstate', () => {
    urlChangeListeners.forEach(listener => listener());
  });
}

// Ensure history is patched when module loads (client-side only)
if (typeof window !== 'undefined') {
  patchHistory();
}

/**
 * Next.js-compatible useSearchParams hook for Vite/React Router.
 *
 * This hook properly syncs with both:
 * 1. React Router DOM navigation (Link, navigate())
 * 2. Direct URL manipulation via window.history.replaceState (used by query-params.ts utilities)
 *
 * Uses useSyncExternalStore to subscribe to URL changes and re-render
 * when the URL changes via any mechanism.
 */
export function useSearchParams() {
  const subscribe = useCallback((callback: () => void) => {
    urlChangeListeners.add(callback);
    return () => {
      urlChangeListeners.delete(callback);
    };
  }, []);

  const getSnapshot = useCallback(() => {
    return window.location.search;
  }, []);

  const getServerSnapshot = useCallback(() => {
    return '';
  }, []);

  // This will cause a re-render whenever the URL search string changes
  const searchString = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Return a URLSearchParams object compatible with Next.js API
  return useMemo(() => new URLSearchParams(searchString), [searchString]);
}

export function useParams() {
  return useRxParams();
}
